import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { BOOKING_CONSENT_MESSAGE, BOOKING_EMAIL_MESSAGE, toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Skeleton } from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import { LegalLink } from '../../reservations/public/PublicFrame';
import { BookingFrame } from './BookingFrame';
import { OfferLine, OfferPanel, type Offer } from './OfferPanel';
import { SlotChooser } from './SlotChooser';
import {
  book,
  createHold,
  isNotFound,
  requestCallback,
  slotTakenOf,
  usePublicPage,
  type BookingHold,
  type BookingTime,
  type PublicPage,
  type SlotTaken,
} from './api';
import { BookingField, BookingSelect, BookingTextarea, ConsentCheckbox } from './fields';
import { slotLabel, slotLabelShort, windowFootnote } from './format';
import { readStored, removeStored, writeStored } from './storage';
import styles from './Booking.module.css';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  concern: string;
  setterName: string;
  setterNote: string;
  /** „Anrede" (D-454): '' = not chosen. */
  salutation: '' | 'Herr' | 'Frau';
  consent: boolean;
}

const emptyForm: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  concern: '',
  setterName: '',
  setterNote: '',
  salutation: '',
  consent: false,
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

const SALUTATION_OPTIONS = [
  { value: '', label: 'Bitte wählen' },
  { value: 'Herr', label: 'Herr' },
  { value: 'Frau', label: 'Frau' },
] as const;

function salutationOf(value: string): FormState['salutation'] {
  return value === 'Herr' || value === 'Frau' ? value : '';
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** The client check of the form; the server answers the same sentences (docs/dev/api-booking.md). */
function validate(form: FormState, withEmail: boolean, setterBooking = false): FieldErrors {
  const errors: FieldErrors = {};
  // A setter booking (D-375): the „Gesprächsnotiz" is required once „Gebucht durch (Setter)" is filled.
  if (setterBooking && form.setterName.trim() && !form.setterNote.trim())
    errors.setterNote = 'Pflichtangabe.';
  if (!form.firstName.trim()) errors.firstName = 'Pflichtangabe.';
  if (!form.lastName.trim()) errors.lastName = 'Pflichtangabe.';
  if (withEmail && !EMAIL.test(form.email.trim())) errors.email = BOOKING_EMAIL_MESSAGE;
  if (!form.phone.trim()) errors.phone = 'Pflichtangabe.';
  if (!form.consent) errors.consent = BOOKING_CONSENT_MESSAGE;
  return errors;
}

function serverFieldErrors(error: unknown): FieldErrors {
  if (!(error instanceof ApiError)) return {};
  const fields = error.fields;
  const result: FieldErrors = {};
  for (const key of [
    'firstName',
    'lastName',
    'email',
    'phone',
    'concern',
    'setterName',
    'setterNote',
    'salutation',
    'consent',
  ] as const) {
    if (fields[key]) result[key] = fields[key];
  }
  return result;
}

export function offerOf(page: PublicPage): Offer {
  return {
    organizationName: page.organization.name,
    advisor: page.advisor,
    eventTypeName: page.eventType.name,
    description: page.eventType.description,
    durationMinutes: page.eventType.durationMinutes,
    durationLabel: page.eventType.durationLabel,
    channelLine: page.eventType.channelLine,
  };
}

/** The race banner of Buchungsseite 03 („14:30 wurde gerade vergeben." + the kept data). */
export function RaceBanner({ race, mobile }: { race: SlotTaken; mobile: boolean }) {
  return (
    <div className={styles.raceBanner} role="alert">
      <span className={styles.raceTitle}>{race.message}</span>
      <span className={styles.raceText}>
        {mobile
          ? 'Ihre Angaben bleiben erhalten.'
          : 'Ihre Angaben bleiben erhalten. Bitte wählen Sie eine andere Zeit.'}
      </span>
    </div>
  );
}

/** 404 / 410 / error pages of the public frame (D-73 pattern). */
export function BookingMessage({ eyebrow, title, text }: { eyebrow: string; title: string; text?: string }) {
  return (
    <div className={styles.narrow}>
      <div className={styles.resultCard}>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <h1 className={styles.resultTitle}>{title}</h1>
        {text ? <p className={styles.resultText}>{text}</p> : null}
      </div>
    </div>
  );
}

export function BookingLoading() {
  return (
    <BookingFrame>
      <div className={styles.card} aria-busy="true" aria-label="Buchungsseite wird geladen">
        <div className={styles.cardLeft}>
          <Skeleton variant="circle" width={52} height={52} />
          <Skeleton width="80%" height={30} />
          <Skeleton height={80} />
        </div>
        <div className={styles.cardRight}>
          <Skeleton height={320} />
        </div>
      </div>
    </BookingFrame>
  );
}

type Step = 'slot' | 'form' | 'callback' | 'callbackDone';

/** Buchungsseite 01–04 (+ 09/10 at 390): `/book/:orgSlug/:typeSlug`. */
export function BookingPage() {
  const { orgSlug = '', typeSlug = '' } = useParams();
  const page = usePublicPage(orgSlug, typeSlug);
  if (page.isPending) return <BookingLoading />;
  if (page.isError) {
    return (
      <BookingFrame>
        {isNotFound(page.error) ? (
          <BookingMessage
            eyebrow="Link nicht gefunden"
            title="Die Buchungsseite wurde nicht gefunden."
            text="Bitte prüfen Sie den Link oder fragen Sie bei Ihrem Ansprechpartner nach."
          />
        ) : (
          <BookingMessage eyebrow="Fehler" title={errorMessage(page.error)} />
        )}
      </BookingFrame>
    );
  }
  return <BookingFlow orgSlug={orgSlug} typeSlug={typeSlug} page={page.data} />;
}

function BookingFlow({ orgSlug, typeSlug, page }: { orgSlug: string; typeSlug: string; page: PublicPage }) {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const storageKey = `${orgSlug}/${typeSlug}`;
  const [step, setStep] = useState<Step>('slot');
  const [hold, setHold] = useState<BookingHold | null>(() => readStored<BookingHold>(storageKey, 'hold'));
  const [form, setForm] = useState<FormState>(() => ({
    ...emptyForm,
    ...readStored<FormState>(storageKey, 'form'),
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [race, setRace] = useState<SlotTaken | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const offer = offerOf(page);
  const host = page.advisor?.name ?? page.organization.name;

  useEffect(() => {
    writeStored(storageKey, 'form', { ...form, consent: false });
  }, [form, storageKey]);

  function update(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(patch) as Array<keyof FormState>) delete next[key];
      return next;
    });
  }

  async function holdTime(time: BookingTime) {
    setBusy(true);
    setNotice(null);
    try {
      const created = await createHold(orgSlug, typeSlug, time.startsAt, hold?.holdKey ?? null);
      setHold(created);
      writeStored(storageKey, 'hold', created);
      setRace(null);
      setStep('form');
    } catch (error) {
      const taken = slotTakenOf(error);
      if (taken) setRace(taken);
      else setNotice(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!hold) {
      setStep('slot');
      return;
    }
    const found = validate(form, true, page.setterBooking);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    setNotice(null);
    try {
      const concern = form.concern.trim();
      const setterName = page.setterBooking ? form.setterName.trim() : '';
      const setterNote = page.setterBooking ? form.setterNote.trim() : '';
      const salutation = page.setterBooking ? form.salutation : '';
      const result = await book(orgSlug, typeSlug, {
        holdKey: hold.holdKey,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        ...(concern ? { concern } : {}),
        ...(setterName ? { setterName, setterNote } : {}),
        ...(salutation ? { salutation } : {}),
        consent: true,
      });
      removeStored(storageKey, 'hold');
      removeStored(storageKey, 'form');
      void navigate(`/book/t/${encodeURIComponent(result.token)}`, {
        replace: true,
        state: { booked: true },
      });
    } catch (error) {
      const taken = slotTakenOf(error);
      if (taken) {
        setRace(taken);
        setStep('slot');
        return;
      }
      const fieldErrors = serverFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }
      if (error instanceof ApiError && (error.fields['holdKey'] || error.fields['startsAt'])) {
        removeStored(storageKey, 'hold');
        setHold(null);
        setNotice(error.fields['holdKey'] ?? error.fields['startsAt'] ?? null);
        setStep('slot');
        return;
      }
      setNotice(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function submitCallback(event: FormEvent) {
    event.preventDefault();
    const found = validate(form, false);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    setNotice(null);
    try {
      await requestCallback(orgSlug, typeSlug, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        consent: true,
      });
      setStep('callbackDone');
    } catch (error) {
      const fieldErrors = serverFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      else setNotice(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  const privacy = page.organization.privacyUrl ? (
    <LegalLink url={page.organization.privacyUrl} title="Datenschutz" className={styles.inlineLink} />
  ) : null;
  const noticeBanner = notice ? (
    <Banner tone="danger" className={styles.notice}>
      {notice}
    </Banner>
  ) : null;
  const raceBanner = race ? <RaceBanner race={race} mobile={mobile} /> : null;

  // --- slot choice (01/03/04) ------------------------------------------------------------------------------
  if (step === 'slot') {
    const chooser = (
      <SlotChooser
        orgSlug={orgSlug}
        typeSlug={typeSlug}
        earliest={page.earliest}
        latest={page.latest}
        mobile={mobile}
        initialDate={hold ? toBerlinDateKey(hold.startsAt) : null}
        initialTime={hold?.startsAt ?? null}
        race={race}
        banner={
          raceBanner || noticeBanner ? (
            <>
              {raceBanner}
              {noticeBanner}
            </>
          ) : null
        }
        footnote={windowFootnote(page.earliest, page.latest)}
        busy={busy}
        mobileLabel={(time) => `Weiter mit ${time.label}`}
        noSlots={{ hostName: host, onCallback: () => setStep('callback') }}
        onContinue={(time) => void holdTime(time)}
      />
    );
    return (
      <BookingFrame organization={page.organization} indexable={page.indexable}>
        {mobile ? (
          <div className={styles.mobileStack}>
            <OfferLine offer={offer} />
            {chooser}
          </div>
        ) : (
          <div className={styles.card}>
            <div className={styles.cardLeft}>
              <OfferPanel offer={offer} />
            </div>
            <div className={styles.cardRight}>{chooser}</div>
          </div>
        )}
      </BookingFrame>
    );
  }

  // --- callback request (04 „Rückruf anfordern") -----------------------------------------------------------
  if (step === 'callback' || step === 'callbackDone') {
    const content =
      step === 'callbackDone' ? (
        <div className={styles.resultCard} role="status">
          <div className={styles.eyebrow}>Rückruf angefordert</div>
          <h1 className={styles.resultTitle}>Vielen Dank.</h1>
          <p className={styles.resultText}>{host} ruft Sie an, sobald etwas frei wird.</p>
        </div>
      ) : (
        <form className={styles.resultCard} onSubmit={(event) => void submitCallback(event)} noValidate>
          <button type="button" className={styles.backLink} onClick={() => setStep('slot')}>
            ‹ Andere Zeit wählen
          </button>
          <h1 className={styles.formTitle}>Rückruf anfordern</h1>
          <p className={styles.resultText}>Oder {host} ruft Sie an, sobald etwas frei wird.</p>
          <div className={styles.formGrid}>
            <BookingField
              label="Vorname"
              required
              autoComplete="given-name"
              value={form.firstName}
              error={errors.firstName}
              onChange={(event) => update({ firstName: event.target.value })}
            />
            <BookingField
              label="Nachname"
              required
              autoComplete="family-name"
              value={form.lastName}
              error={errors.lastName}
              onChange={(event) => update({ lastName: event.target.value })}
            />
            <BookingField
              label="Telefon"
              required
              type="tel"
              autoComplete="tel"
              value={form.phone}
              error={errors.phone}
              onChange={(event) => update({ phone: event.target.value })}
            />
          </div>
          <ConsentCheckbox
            checked={form.consent}
            error={errors.consent}
            privacy={privacy}
            onChange={(consent) => update({ consent })}
          />
          {noticeBanner}
          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.primaryButtonLarge}
              disabled={busy}
              aria-busy={busy || undefined}
            >
              Rückruf anfordern
            </button>
          </div>
        </form>
      );
    return (
      <BookingFrame organization={page.organization} indexable={page.indexable}>
        <div className={styles.narrow}>{content}</div>
      </BookingFrame>
    );
  }

  // --- form (02, 10 „Angaben") -----------------------------------------------------------------------------
  const fields = (
    <>
      <div className={clsx(styles.formGrid, mobile && styles.formGridMobile)}>
        <BookingField
          label="Vorname"
          required
          autoComplete="given-name"
          value={form.firstName}
          error={errors.firstName}
          onChange={(event) => update({ firstName: event.target.value })}
        />
        <BookingField
          label="Nachname"
          required
          autoComplete="family-name"
          value={form.lastName}
          error={errors.lastName}
          onChange={(event) => update({ lastName: event.target.value })}
        />
        <BookingField
          label="E-Mail"
          required
          type="email"
          autoComplete="email"
          value={form.email}
          error={errors.email}
          onChange={(event) => update({ email: event.target.value })}
        />
        <BookingField
          label="Telefon"
          required
          type="tel"
          autoComplete="tel"
          value={form.phone}
          error={errors.phone}
          hint={mobile ? undefined : 'Für Rückfragen, falls die Verbindung ausfällt.'}
          onChange={(event) => update({ phone: event.target.value })}
        />
      </div>
      <BookingTextarea
        label="Worum soll es gehen? (optional)"
        className={styles.concern}
        rows={2}
        maxLength={2000}
        value={form.concern}
        onChange={(event) => update({ concern: event.target.value })}
      />
      {page.setterBooking ? (
        <>
          <BookingSelect
            label="Anrede (optional)"
            className={styles.concern}
            value={form.salutation}
            error={errors.salutation}
            hint="Leer lassen: DAMOKLES ermittelt die Anrede aus dem Gespräch."
            options={SALUTATION_OPTIONS}
            onChange={(event) => update({ salutation: salutationOf(event.target.value) })}
          />
          <BookingField
            label="Gebucht durch (Setter)"
            className={styles.concern}
            autoComplete="off"
            maxLength={100}
            value={form.setterName}
            error={errors.setterName}
            hint="Leer lassen, wenn Sie selbst buchen."
            onChange={(event) => update({ setterName: event.target.value })}
          />
          <BookingTextarea
            label="Gesprächsnotiz"
            className={styles.concern}
            rows={4}
            maxLength={2000}
            required={form.setterName.trim() !== ''}
            value={form.setterNote}
            error={errors.setterNote}
            hint="Was den Kunden bewogen hat, dem Termin zuzustimmen."
            onChange={(event) => update({ setterNote: event.target.value })}
          />
        </>
      ) : null}
      <ConsentCheckbox
        checked={form.consent}
        error={errors.consent}
        privacy={privacy}
        onChange={(consent) => update({ consent })}
      />
      {noticeBanner}
    </>
  );

  return (
    <BookingFrame organization={page.organization} indexable={page.indexable}>
      {mobile ? (
        <form className={styles.mobileStack} onSubmit={(event) => void submit(event)} noValidate>
          <OfferLine offer={offer} />
          {hold ? (
            <div className={styles.slotChip}>
              <span className={styles.slotChipText}>{slotLabelShort(hold.startsAt, hold.endsAt)}</span>
              <button type="button" className={styles.textLink} onClick={() => setStep('slot')}>
                ändern
              </button>
            </div>
          ) : null}
          {fields}
          <div className={styles.sticky}>
            <button
              type="submit"
              className={styles.stickyPrimary}
              disabled={busy}
              aria-busy={busy || undefined}
            >
              Termin buchen
            </button>
            <span className={styles.stickyNote}>Die Zeit ist 10 Minuten für Sie reserviert.</span>
          </div>
        </form>
      ) : (
        <div className={styles.card}>
          <div className={styles.cardLeft}>
            <OfferPanel
              offer={offer}
              appointmentLabel={hold ? slotLabel(hold.startsAt, hold.endsAt) : null}
            />
          </div>
          <form className={styles.cardRight} onSubmit={(event) => void submit(event)} noValidate>
            <div>
              <button type="button" className={styles.backLink} onClick={() => setStep('slot')}>
                ‹ Andere Zeit wählen
              </button>
            </div>
            <h2 className={styles.formTitle}>Ihre Angaben</h2>
            {fields}
            <div className={styles.formActions}>
              <span className={styles.formNote}>Die Zeit ist 10 Minuten für Sie reserviert.</span>
              <button
                type="submit"
                className={styles.primaryButtonLarge}
                disabled={busy}
                aria-busy={busy || undefined}
              >
                Termin buchen
              </button>
            </div>
          </form>
        </div>
      )}
    </BookingFrame>
  );
}
